import { convertToFirstLetterCapital, isEmpty } from '../../utils/utils';
import { CONFIG_TYPE, PEC_SELECTED_FIELDS_KEYS } from './DealDrawerConstants';

export type PecFieldConfig = {
    type?: string;
    importance?: boolean;
    isRequired?: boolean;
    label?: string;
};

export type PecSection = {
    sectionKey?: string;
    text?: string;
    config?: Record<string, PecFieldConfig>;
};

export type PecPickerOption = { label: string; value: string };

/** The flat formData key holding a step's selection, or undefined for a section that has none. */
export const selectionStorageKey = (sectionKey?: string): string | undefined =>
    sectionKey ? PEC_SELECTED_FIELDS_KEYS[sectionKey] : undefined;

/** The config types whose inputs write a minimum/maximum pair instead of a single key. */
const RANGE_CONFIG_TYPES: string[] = [CONFIG_TYPE.RANGE, CONFIG_TYPE.RANGE_FILTER];

/**
 * Every formData key a single config field owns, derived the same way InputRenderer derives them
 * so picker and renderer cannot drift.
 * convertToFirstLetterCapital lowercases the tail, so companyAge owns minimumCompanyage /
 * maximumCompanyage / importanceCompanyage — not ...CompanyAge.
 */
export function expandFieldKeys(sectionKey: string, fieldKey: string, fieldConfig?: PecFieldConfig): string[] {
    const capitalised = convertToFirstLetterCapital(fieldKey);
    const prefixed = (key: string) => (sectionKey ? `${sectionKey}.${key}` : key);
    const keys = RANGE_CONFIG_TYPES.includes(fieldConfig?.type ?? '')
        ? [prefixed(`minimum${capitalised}`), prefixed(`maximum${capitalised}`)]
        : [prefixed(fieldKey)];
    if (fieldConfig?.importance === true) keys.push(prefixed(`importance${capitalised}`));
    return keys;
}

/** '' | null | undefined | [] all count as unconfigured — handleChange does not delete empty arrays. */
export function isFieldConfigured(formData: Record<string, any>, expandedKeys: string[]): boolean {
    return expandedKeys.some((key) => {
        const value = formData?.[key];
        if (Array.isArray(value)) return value.length > 0;
        return !isEmpty(value);
    });
}

/** Fields that render outside the picker-controlled set: config-derived, never named in code. */
export function getAlwaysOnFieldKeys(section?: PecSection): string[] {
    return Object.entries(section?.config ?? {})
        .filter(([, fieldConfig]) => fieldConfig?.isRequired === true)
        .map(([fieldKey]) => fieldKey);
}

/**
 * Backfill for documents saved before the picker existed: a field counts as selected when any
 * key it owns holds a value. Always-on fields are excluded — they are never stored members of the
 * selection, and a stored always-on key would render as an empty chip (the chip label is an
 * options lookup, and the picker's options exclude always-on fields).
 */
export function deriveSelectedFields(section: PecSection | undefined, formData: Record<string, any>): string[] {
    const sectionKey = section?.sectionKey ?? '';
    const alwaysOn = new Set(getAlwaysOnFieldKeys(section));
    return Object.entries(section?.config ?? {})
        .filter(([fieldKey]) => !alwaysOn.has(fieldKey))
        .filter(([fieldKey, fieldConfig]) => isFieldConfigured(formData, expandFieldKeys(sectionKey, fieldKey, fieldConfig)))
        .map(([fieldKey]) => fieldKey);
}

/** Selection in the user's pick order — never sorted, never re-derived from config order. */
export function resolveSelectedFields(section: PecSection | undefined, formData: Record<string, any>): string[] {
    const storageKey = selectionStorageKey(section?.sectionKey);
    const stored = storageKey ? formData?.[storageKey] : undefined;
    const configKeys = new Set(Object.keys(section?.config ?? {}));
    const alwaysOn = new Set(getAlwaysOnFieldKeys(section));
    // Array.isArray, not truthiness: [] is an explicit "nothing selected", not "never set".
    const base = Array.isArray(stored) ? stored : deriveSelectedFields(section, formData);
    return base.filter((fieldKey: string) => configKeys.has(fieldKey)).filter((fieldKey: string) => !alwaysOn.has(fieldKey));
}

/** Rendered set = always-on ∪ selected, in config order. Unselected fields are omitted, never hidden. */
export function buildFilteredSectionConfig(section: PecSection, selected: string[], alwaysOn: string[]): PecSection {
    const keep = new Set([...alwaysOn, ...selected]);
    const config = Object.fromEntries(Object.entries(section?.config ?? {}).filter(([fieldKey]) => keep.has(fieldKey)));
    return { ...section, config };
}

/** Picker options in config order, excluding always-on fields, which cannot be unselected. */
export function buildPickerOptions(section?: PecSection): PecPickerOption[] {
    const alwaysOn = new Set(getAlwaysOnFieldKeys(section));
    return Object.entries(section?.config ?? {})
        .filter(([fieldKey]) => !alwaysOn.has(fieldKey))
        .map(([fieldKey, fieldConfig]) => ({ label: fieldConfig?.label ?? fieldKey, value: fieldKey }));
}
